import React, { useState, useEffect } from 'react';
import { getEmergencyResources } from '../services/api';
import DisclaimerBanner from '../components/DisclaimerBanner';

const Emergency = () => {
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedState, setSelectedState] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const filters = selectedState ? { state: selectedState } : {};
        const data = await getEmergencyResources(filters);
        setResources(data);
      } catch (error) {
        console.error('Failed to fetch emergency resources:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [selectedState]);

  const nationalResources = resources.filter((r) => r.scope === 'national');
  const stateResources = resources.filter((r) => r.scope === 'state' || r.scope === 'district');

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white" data-testid="emergency-page">
      {/* Full Disclaimer */}
      <DisclaimerBanner variant="full" />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Page Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Mental Health Support Resources
          </h1>
          <p className="text-lg text-gray-600 leading-relaxed max-w-2xl mx-auto">
            Find verified helplines and support services across India. These resources are here to help you or someone you care about.
          </p>
        </div>

        {/* National Helplines */}
        <div className="mb-12">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-1 h-8 bg-gradient-to-b from-teal-500 to-blue-500 rounded-full"></div>
            <h2 className="text-2xl font-bold text-gray-900">
              National Helplines
            </h2>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="spinner mx-auto mb-4"></div>
              <p className="text-gray-600">Loading resources...</p>
            </div>
          ) : nationalResources.length === 0 ? (
            <div className="card text-center py-12">
              <p className="text-gray-600">Emergency resources will be available soon.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {nationalResources.map((resource) => (
                <div
                  key={resource.id}
                  className="card hover:shadow-lg transition-shadow"
                  data-testid="emergency-resource-card"
                >
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center">
                      <span className="text-2xl">☎️</span>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-gray-900 mb-2" data-testid="emergency-resource-name">
                        {resource.name}
                      </h3>
                      <p className="text-gray-700 mb-4 leading-relaxed" data-testid="emergency-resource-description">
                        {resource.description}
                      </p>
                      <div className="space-y-3">
                        <div className="flex items-center gap-3 p-3 bg-teal-50 rounded-lg border border-teal-200">
                          <span className="text-teal-600 text-xl">📞</span>
                          <div>
                            <p className="text-sm text-teal-700 font-medium">Phone Number</p>
                            <a
                              href={`tel:${resource.phone}`}
                              className="text-lg font-bold text-teal-600 hover:text-teal-700"
                              data-testid="emergency-resource-phone"
                            >
                              {resource.phone}
                            </a>
                          </div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                          <div className="flex items-center gap-2 text-gray-700">
                            <span>🕐</span>
                            <span data-testid="emergency-resource-hours">{resource.available_hours}</span>
                          </div>
                          {resource.languages.length > 0 && (
                            <div className="flex items-start gap-2 text-gray-700">
                              <span>💬</span>
                              <span>Languages: {resource.languages.join(', ')}</span>
                            </div>
                          )}
                        </div>
                        {resource.website && (
                          <a
                            href={resource.website}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 text-teal-600 hover:text-teal-700 font-medium text-sm"
                          >
                            🌐 Visit Website →
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* State-Specific Resources */}
        {stateResources.length > 0 && (
          <div>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-1 h-8 bg-gradient-to-b from-blue-500 to-teal-500 rounded-full"></div>
              <h2 className="text-2xl font-bold text-gray-900">
                State-Specific Resources
              </h2>
            </div>
            <div className="space-y-4">
              {stateResources.map((resource) => (
                <div
                  key={resource.id}
                  className="card hover:shadow-lg transition-shadow"
                  data-testid="state-emergency-resource"
                >
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                      <span className="text-2xl">📍</span>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-start justify-between mb-2">
                        <h3 className="text-xl font-bold text-gray-900">{resource.name}</h3>
                        {resource.state && (
                          <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium border border-blue-200">
                            {resource.state}
                          </span>
                        )}
                      </div>
                      <p className="text-gray-700 mb-4 leading-relaxed">{resource.description}</p>
                      <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                        <span className="text-blue-600 text-xl">📞</span>
                        <div>
                          <p className="text-sm text-blue-700 font-medium">Phone Number</p>
                          <a
                            href={`tel:${resource.phone}`}
                            className="text-lg font-bold text-blue-600 hover:text-blue-700"
                          >
                            {resource.phone}
                          </a>
                        </div>
                      </div>
                      <div className="mt-3 flex items-center gap-2 text-sm text-gray-700">
                        <span>🕐</span>
                        <span>{resource.available_hours}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Supportive Closing Message */}
        <div className="mt-12 card bg-gradient-to-r from-teal-50 to-blue-50 border-2 border-teal-200">
          <div className="text-center py-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-3">You're Not Alone</h3>
            <p className="text-gray-700 leading-relaxed max-w-2xl mx-auto">
              Reaching out for help is a sign of strength. These services are confidential, professional, and here to support you. It's okay to ask for help.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Emergency;
